import { createContext, useContext, useState, ReactNode } from 'react';

interface User {
  email: string;
}

interface UserContextType {
  user: User;
  setUserEmail: (email: string) => void;
  handleSetUserEmail: () => void;
  /** Set email and persist (e.g. on login). Use when you have the final value. */
  login: (email: string) => void;
  /** Clear user email and persist. Returns to login. */
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<User>({
    email: localStorage.getItem('userEmail') || '',
  });

  const setUserEmail = (email: string) => {
    setUser((prev) => ({ ...prev, email }));
  };

  const handleSetUserEmail = () => {
    if (user.email.trim()) {
      localStorage.setItem('userEmail', user.email.trim());
    } else {
      localStorage.removeItem('userEmail');
    }
  };

  const login = (email: string) => {
    const trimmed = email.trim();
    if (trimmed) {
      localStorage.setItem('userEmail', trimmed);
      setUser({ email: trimmed });
    }
  };

  const logout = () => {
    localStorage.removeItem('userEmail');
    setUser({ email: '' });
  };

  const value: UserContextType = {
    user,
    setUserEmail,
    handleSetUserEmail,
    login,
    logout,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
